import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoggingService, StaticContentService } from '@mwe/services';
import { Notification } from '@mwe/models';
import { catchError, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { getRouteData } from '@mwe/utils';
import dayjs from 'dayjs';

const NOTIFICATION_PRIORITY = [
  'danger-bold',
  'danger-subtle',
  'warning-bold',
  'warning-subtle',
  'info-bold',
  'info-subtle',
  'neutral-bold',
  'neutral-subtle',
];

const SESSION_STORAGE_KEY = 'global-notification-banner-closed';

export const GlobalNotificationStore = signalStore(
  { providedIn: 'root' },
  withState({
    _notifications: [] as Notification[],
  }),
  withProps(() => ({
    _activatedRoute: inject(ActivatedRoute),
    _staticContentService: inject(StaticContentService),
    _loggingService: inject(LoggingService),
  })),
  withComputed(store => {
    const notifications = computed(() => {
      return store._notifications().toSorted((a, b) => {
        const getPriority = (n: Notification) => {
          const index = NOTIFICATION_PRIORITY.indexOf(`${n.type}-${n.emphasis}`);
          return index === -1 ? 999 : index;
        };
        return getPriority(a) - getPriority(b);
      });
    });

    return { notifications };
  }),
  withMethods(store => {
    const addNotification = (notification: Notification) => {
      patchState(store, { _notifications: [...store._notifications(), notification] });
    };

    const removeNotification = (notification: Notification) => {
      patchState(store, { _notifications: store._notifications().filter(n => n !== notification) });
    };

    const _staticNotifications = () => {
      store._staticContentService
        .getGlobalNotification()
        .pipe(
          tap(n => {
            // If Notifications are empty, return
            if (!Object.entries(n)?.length) return;

            // Check whether the banner was closed before
            const hasUserClosedBanner = sessionStorage.getItem(SESSION_STORAGE_KEY);
            if (hasUserClosedBanner) return;

            const { validFrom, validTo, hideOnFrontpage, ...rest } = n;
            const routeData = getRouteData(store._activatedRoute);
            const isLandingPage = routeData?.isLanding;

            // If the Display Conditions are not met, return
            if (isLandingPage && hideOnFrontpage) return;

            const now = dayjs();
            if (validFrom && dayjs(validFrom).isAfter(now)) return;
            if (validTo && dayjs(validTo).isBefore(now)) return;

            const notification = {
              ...rest,
              isClosable: true,
              closeButtonTitle: 'global.notification.buttonTitle',
              closeCallback: () => sessionStorage.setItem(SESSION_STORAGE_KEY, 'true'),
            } satisfies Notification;

            patchState(store, { _notifications: [...store._notifications(), notification] });
          }),
          catchError(err => {
            store._loggingService.logError(err, 'Error loading global notification from Static');
            return of(null);
          }),
        )
        .subscribe();
    };

    return {
      addNotification,
      removeNotification,
      _staticNotifications,
    };
  }),
  withHooks(store => {
    return {
      onInit() {
        store._staticNotifications();
      },
    };
  }),
);
